import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Kanban from "@/components/crm/Kanban";
import { LeadTable, LeadCards } from "@/components/crm/LeadViews";
import TasksView from "@/components/crm/TasksView";
import LeadDrawer from "@/components/crm/LeadDrawer";
import NewLeadDialog from "@/components/crm/NewLeadDialog";
import MetricsBar from "@/components/crm/MetricsBar";
import Pager from "@/components/crm/Pager";
import { useLiveRev } from "@/lib/live";
import { cached, remember } from "@/lib/cache";
import { FilterBuilder, ColumnPicker } from "@/components/crm/ViewControls";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Rows } from "@/components/ui-helpers";
import { rawValue } from "@/components/crm/LeadViews";
import { Columns3, List, LayoutGrid, CheckSquare, Plus, Search, Save, Trash2, Download, X, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const LAYOUTS = [
  { k: "list", label: "List", icon: List }, { k: "kanban", label: "Board", icon: Columns3 },
  { k: "cards", label: "Cards", icon: LayoutGrid }, { k: "tasks", label: "Tasks", icon: CheckSquare },
];
const pick = (v) => ({ name: v.name, layout: v.layout, filters: v.filters || [], columns: v.columns || [], sort: v.sort, match: v.match || "all" });

function ViewTabs({ views, active, onPick, onRename, onDuplicate, onDelete }) {
  return (
    <div className="flex gap-1 overflow-x-auto px-4 md:px-6 mt-3 border-b border-[var(--border)] text-sm" data-testid="views-tabs">
      {views.map((v) => (
        <div key={v._id} className={`flex items-center border-b-2 -mb-px transition-colors ${active === v._id ? "border-[var(--tab-line)] text-[var(--text)] font-medium" : "border-transparent text-[var(--text-dim)] hover:text-[var(--text)]"}`}>
          <button onClick={() => onPick(v._id)} data-testid={`view-tab-${v._id}`} className="pl-3 pr-1 py-2.5 whitespace-nowrap">{v.name}</button>
          {active === v._id ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild><button className="p-1 mr-1 rounded hover:bg-[var(--panel-2)]" data-testid={`view-menu-${v._id}`}><MoreHorizontal size={13} /></button></DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {v._id !== "default" && <DropdownMenuItem onClick={onRename} data-testid="view-rename-item">Rename</DropdownMenuItem>}
                <DropdownMenuItem onClick={onDuplicate} data-testid="view-duplicate-item">Duplicate</DropdownMenuItem>
                {v._id !== "default" && <DropdownMenuItem onClick={onDelete} className="text-red-400" data-testid="view-delete-item">Delete</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : <span className="w-2" />}
        </div>
      ))}
    </div>
  );
}

function StageChips({ stages, counts, colors, active, onPick }) {
  return (
    <div className="flex flex-wrap gap-2 px-4 md:px-6 pb-4" data-testid="stage-chips" title="Shift-click to select several stages">
      {stages.filter((s) => counts[s] || active.includes(s)).map((s) => {
        const c = colors[s] || "#6b7280"; const on = active.includes(s);
        return (
          <button key={s} onClick={(e) => onPick(s, e.shiftKey || e.metaKey || e.ctrlKey)} data-testid={`stage-chip-${s}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[13px] border transition-colors hover:border-[var(--border-strong)]"
            style={{ borderColor: on ? c : "var(--border)", background: on ? `${c}26` : "var(--panel)", color: on ? c : "var(--text-dim)" }}>
            <span className="w-2 h-2 rounded-full" style={{ background: c }} />{s}<span className="font-mono text-xs text-[var(--text-faint)]">{counts[s]}</span>
          </button>
        );
      })}
    </div>
  );
}

function toCsv(leads, cols) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [cols.map((c) => esc(c.label)).join(","), ...leads.map((l) => cols.map((c) => {
    const v = rawValue(l, c.key);
    return esc(Array.isArray(v) ? v.join("; ") : v);
  }).join(","))];
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
  a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
}

function BulkBar({ ids, stages, users, onDone, onClear, isAdmin }) {
  const { confirm } = useConfirm();
  const run = async (body, label) => {
    const { data } = await api.post("/crm/leads/bulk", { ids, ...body });
    toast.success(`${label} · ${data.updated} leads`); onDone();
  };
  const del = async () => { if (await confirm({ title: `Delete ${ids.length} leads?`, body: "Their tasks, meetings and timeline are deleted too.", danger: true })) run({ delete: true }, "Deleted"); };
  return (
    <div className="flex flex-wrap items-center gap-2 mx-4 md:mx-6 mb-3 px-3 py-2 rounded border border-[var(--ai)] bg-[var(--panel)] text-xs" data-testid="bulk-bar">
      <span className="font-medium">{ids.length} selected</span>
      <select className="px-2 py-1 rounded border text-xs" value="" onChange={(e) => e.target.value && run({ stage: e.target.value }, "Stage updated")} data-testid="bulk-stage">
        <option value="">Move to stage…</option>{stages.map((s) => <option key={s}>{s}</option>)}
      </select>
      <select className="px-2 py-1 rounded border text-xs" value="" onChange={(e) => e.target.value && run({ owner: e.target.value }, "Owner updated")} data-testid="bulk-owner">
        <option value="">Assign owner…</option>{users.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
      </select>
      {isAdmin && <button className="btn text-xs hover:text-[var(--breach)]" onClick={del} data-testid="bulk-delete"><Trash2 size={12} />Delete</button>}
      <button className="btn-ghost btn text-xs ml-auto" onClick={onClear} data-testid="bulk-clear"><X size={12} />Clear</button>
    </div>
  );
}

export default function CRM({ module, title = "CRM", extraFields }) {
  const vkey = module ? `crm_active_view_${module}` : "crm_active_view";
  const { user } = useAuth();
  const { confirm, prompt } = useConfirm();
  const [params, setParams] = useSearchParams();
  const [baseCfg, setCfg] = useState(cached("crm_cfg") || null);
  const cfg = useMemo(() => (baseCfg && extraFields?.length ? {
    ...baseCfg, fields: [...baseCfg.fields, ...extraFields.filter((f) => f.key && !baseCfg.fields.some((c) => c.key === f.key)).map((f) => ({ ...f, show_in_list: true }))],
  } : baseCfg), [baseCfg, extraFields]);
  const [users, setUsers] = useState(cached("crm_users") || []);
  const [saved, setSaved] = useState(cached("crm_views") || []);
  const [active, setActive] = useState(localStorage.getItem(module ? `crm_active_view_${module}` : "crm_active_view") || "default");
  const [draft, setDraft] = useState(null);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [selected, setSelected] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(Number(localStorage.getItem("crm_page_size")) || 50);
  const [stageCounts, setStageCounts] = useState({});
  const leadId = params.get("lead");
  useEffect(() => { if (params.get("new")) { setNewOpen(true); setParams({}); } }, [params, setParams]);

  const defaultView = useMemo(() => cfg && ({
    _id: "default", name: "All leads", layout: "list", filters: [],
    columns: ["name", "next_task", ...cfg.fields.filter((f) => f.show_in_list && f.key !== "name").map((f) => f.key)], sort: { field: "next_task", dir: "asc" },
  }), [cfg]);
  const views = useMemo(() => (defaultView ? [defaultView, ...saved] : []), [defaultView, saved]);
  const current = views.find((v) => v._id === active) || defaultView;

  useEffect(() => {
    api.get("/crm/config").then((r) => setCfg(remember("crm_cfg", r.data)));
    api.get("/crm/users").then((r) => setUsers(remember("crm_users", r.data.rows)));
    api.get("/crm/views").then((r) => setSaved(remember("crm_views", r.data.rows)));
  }, []);
  const [mobileCards, setMobileCards] = useState(false);
  useEffect(() => {
    if (!current) return;
    const p = pick(current);
    const force = window.innerWidth < 768 && p.layout === "list";
    if (force) p.layout = "cards";
    setMobileCards(force);
    setDraft(p);
  }, [current?._id, defaultView]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLeads = useCallback(() => {
    if (!draft) return;
    const paged = ["list", "cards"].includes(draft.layout);
    const body = { filters: draft.filters, match: draft.match, sort: draft.sort, q: q || undefined, limit: paged ? size : 2000, skip: paged ? page * size : 0, module: module || undefined };
    const key = `crm_leads:${JSON.stringify(body)}`;
    api.post("/crm/leads/query", body)
      .then((r) => { remember(key, r.data); applyLeads(r.data); setSelected((s) => s.filter((id) => r.data.rows.some((l) => l._id === id))); });
  }, [draft, q, page, size, module]); // eslint-disable-line react-hooks/exhaustive-deps
  const applyLeads = (d) => { setLeads(d.rows); setTotal(d.total); setStageCounts(d.stage_counts || {}); setLoaded(true); };
  useEffect(() => { setPage(0); }, [draft?.filters, draft?.sort, draft?.layout, q, size]);
  useEffect(() => {
    if (draft) {
      const paged = ["list", "cards"].includes(draft.layout);
      const hit = cached(`crm_leads:${JSON.stringify({ filters: draft.filters, match: draft.match, sort: draft.sort, q: q || undefined, limit: paged ? size : 2000, skip: paged ? page * size : 0, module: module || undefined })}`);
      if (hit) applyLeads(hit);
    }
    const t = setTimeout(loadLeads, 200);
    return () => clearTimeout(t);
  }, [loadLeads]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = draft && current && JSON.stringify(mobileCards && draft.layout === "cards" ? { ...draft, layout: "list" } : draft) !== JSON.stringify(pick(current));
  const pickView = (id) => { setActive(id); localStorage.setItem(vkey, id); };
  const setD = (p) => setDraft((d) => ({ ...d, ...p }));
  const onSort = (field) => setD({ sort: { field, dir: draft.sort?.field === field && draft.sort.dir === "desc" ? "asc" : "desc" } });

  const saveView = async (asNew) => {
    try {
      if (asNew || active === "default") {
        const name = await prompt({ title: "Name this view", defaultValue: asNew ? `${draft.name} copy` : "My view", okLabel: "Save view" });
        if (!name) return;
        const { data } = await api.post("/crm/views", { ...draft, name });
        setSaved((s) => [...s, data]); pickView(data._id);
      } else {
        const { data } = await api.put(`/crm/views/${active}`, draft);
        setSaved((s) => s.map((v) => (v._id === active ? data : v)));
      }
      toast.success("View saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save view"); }
  };
  const deleteView = async () => {
    if (!(await confirm({ title: `Delete view "${current.name}"?`, danger: true }))) return;
    await api.delete(`/crm/views/${active}`);
    setSaved((s) => s.filter((v) => v._id !== active)); pickView("default");
  };
  const renameView = async () => {
    const name = await prompt({ title: "Rename view", defaultValue: current.name, okLabel: "Rename" });
    if (!name || name === current.name) return;
    try {
      const { data } = await api.put(`/crm/views/${active}`, { ...pick(current), name });
      setSaved((s) => s.map((v) => (v._id === active ? data : v))); toast.success("View renamed");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't rename"); }
  };
  const duplicateView = async () => {
    try {
      const { data } = await api.post("/crm/views", { ...draft, name: `${current.name} copy` });
      setSaved((s) => [...s, data]); pickView(data._id); toast.success("View duplicated");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't duplicate"); }
  };

  const openLead = (id) => setParams(id ? { lead: id } : {});
  const closeLead = useCallback(() => setParams({}), [setParams]);
  const changed = useCallback(() => { loadLeads(); setTick((t) => t + 1); }, [loadLeads]);
  useLiveRev(changed);
  const move = async (id, stage) => {
    setLeads((ls) => ls.map((l) => (l._id === id ? { ...l, stage } : l)));
    try { await api.put(`/crm/leads/${id}`, { stage }); changed(); } catch (e) { if (!e.response?.data?.approval_required) toast.error("Couldn't move lead"); loadLeads(); }
  };

  if (!cfg || !draft) return <div className="p-6"><Rows n={8} /></div>;
  const colors = cfg.stage_colors || {};
  const isStage = (f) => f.field === "stage" && f.op === "is";
  const stageFilter = draft.filters.filter(isStage).flatMap((f) => (Array.isArray(f.value) ? f.value : f.value ? [f.value] : []));
  const pickStage = (s, multi) => {
    const on = stageFilter.includes(s);
    const next = multi ? (on ? stageFilter.filter((x) => x !== s) : [...stageFilter, s]) : (on && stageFilter.length === 1 ? [] : [s]);
    setD({ filters: [...draft.filters.filter((f) => !isStage(f)), ...(next.length ? [{ field: "stage", op: "is", value: next }] : [])] });
  };
  const allFields = cfg.fields;
  const exportCsv = () => toCsv(selected.length ? leads.filter((l) => selected.includes(l._id)) : leads,
    (draft.layout === "list" ? draft.columns : allFields.map((f) => f.key)).map((k) => allFields.find((f) => f.key === k) || { key: k, label: k }));
  const order = leads.map((l) => l._id);
  const step = (dir) => { const i = order.indexOf(leadId); const n = order[i + dir]; if (n) openLead(n); };

  return (
    <div data-testid="crm-page">
      <div className="flex flex-wrap md:flex-nowrap items-center gap-x-6 gap-y-2 px-4 md:px-6 py-3 border-b border-[var(--border)]">
        <h1 className="text-xl font-semibold tracking-tight" data-testid="page-title">{title}</h1>
        <button className="btn btn-primary ml-auto md:order-3" onClick={() => setNewOpen(true)} data-testid="new-lead-btn"><Plus size={14} />Lead</button>
        <div className="w-full md:w-auto md:flex-1 min-w-0 md:order-2">{!module && <MetricsBar userEmail={user?.email} refreshKey={tick} />}</div>
      </div>
      <ViewTabs views={views} active={current._id} onPick={pickView} onRename={renameView} onDuplicate={duplicateView} onDelete={deleteView} />
      <div className="flex flex-wrap items-center gap-2 px-4 md:px-6 py-3">
        <div className="flex rounded-md border border-[var(--border-strong)] overflow-hidden" data-testid="view-switcher">
          {LAYOUTS.map(({ k, label, icon: I }) => (
            <button key={k} onClick={() => setD({ layout: k })} data-testid={`view-${k}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-[13px] transition-colors ${draft.layout === k ? "bg-white text-black font-medium" : "text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--hover)]"}`}>
              <I size={14} /><span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
        {draft.layout !== "tasks" && (
          <>
            <div className="relative flex-1 min-w-[180px] max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-faint)]" />
              <input className="w-full pl-9 pr-3 py-2 rounded-md border text-sm" placeholder="Search name, email, phone" value={q} onChange={(e) => setQ(e.target.value)} data-testid="crm-search" />
            </div>
            <FilterBuilder filters={draft.filters} onChange={(filters) => setD({ filters })} match={draft.match} onMatch={(match) => setD({ match })} fields={cfg.fields} stages={cfg.stages} users={users} />
            {draft.layout === "list" && <ColumnPicker columns={draft.columns} onChange={(columns) => setD({ columns })} fields={cfg.fields} />}
          </>
        )}
        {dirty && <button className="btn btn-primary text-xs" onClick={() => saveView(false)} data-testid="save-view-btn"><Save size={13} />{active === "default" ? "Save as view" : "Save view"}</button>}
        {active !== "default" && <button className="btn-ghost btn text-xs" onClick={() => saveView(true)} data-testid="save-view-as-btn">Save as new</button>}
        {active !== "default" && <button className="btn-ghost btn text-xs hover:text-[var(--breach)]" onClick={deleteView} data-testid="delete-view-btn"><Trash2 size={13} /></button>}
        {draft.layout !== "tasks" && <button className="btn-ghost btn text-xs ml-auto" onClick={exportCsv} data-testid="export-csv-btn"><Download size={13} />CSV</button>}
        {draft.layout !== "tasks" && <span className="text-sm text-[var(--text-dim)]" data-testid="crm-count"><b className="text-[var(--text)] font-semibold">{total}</b> lead{total === 1 ? "" : "s"}</span>}
      </div>
      {draft.layout !== "tasks" && <StageChips stages={cfg.stages} counts={stageCounts} colors={colors} active={stageFilter} onPick={pickStage} />}
      {selected.length > 0 && <BulkBar ids={selected} stages={cfg.stages} users={users} isAdmin={user?.role === "admin"} onClear={() => setSelected([])} onDone={() => { setSelected([]); changed(); }} />}
      <div className="px-4 md:px-6 pb-6">
        {!loaded && draft.layout !== "tasks" && <Rows n={8} />}
        {loaded && draft.layout === "list" && <LeadTable leads={leads} fields={cfg.fields} columns={draft.columns} onOpen={openLead} users={users} colors={colors} sort={draft.sort} onSort={onSort}
          selected={selected} onSelect={setSelected} onCreate={() => setNewOpen(true)} />}
        {loaded && draft.layout === "kanban" && <Kanban stages={cfg.stages} leads={leads} onOpen={openLead} onMove={move} users={users} />}
        {loaded && draft.layout === "cards" && <LeadCards leads={leads} onOpen={openLead} users={users} colors={colors} />}
        {loaded && ["list", "cards"].includes(draft.layout) && total > 0 && <div className={draft.layout === "cards" ? "mt-3" : ""}><Pager page={page} size={size} total={total} onPage={setPage} onSize={(n) => { localStorage.setItem("crm_page_size", n); setSize(n); }} /></div>}
        {draft.layout === "tasks" && <TasksView cadence={cfg.cadence} onOpen={openLead} refreshKey={tick} onChanged={loadLeads} />}
      </div>
      <LeadDrawer leadId={leadId} cfg={cfg} users={users} onClose={closeLead} onChanged={changed}
        onPrev={order.indexOf(leadId) > 0 ? () => step(-1) : null} onNext={order.indexOf(leadId) >= 0 && order.indexOf(leadId) < order.length - 1 ? () => step(1) : null} />
      <NewLeadDialog open={newOpen} onClose={() => setNewOpen(false)} stages={cfg.stages} users={users} module={module}
        onCreated={(id) => { setNewOpen(false); changed(); openLead(id); }} />
    </div>
  );
}
